import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { fieldRequired } from '../../util/constants';
import { validateAuth } from '../../util/util.js';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { userTypeSelector } from '../../redux/selectors.js';
import { FAQ_Categories } from '../../util/constants';
import { notificationSuccess, notificationError } from '../../redux/actions/app.js';

const QUESTION_MAX_CHAR_LIMIT = 100;
const ANSWER_MAX_CHAR_LIMIT = 2500;

export default function FaqModal({ open, onClose, selectedFaq = null }) {
  const [formValues, setFormValues] = useState({
    question: selectedFaq?.question || '',
    answer: selectedFaq?.answer || '',
    category: selectedFaq?.category || '',
    weight: selectedFaq?.weight || null,
  });
  const [errors, setErrors] = useState({});
  const [weightOptions, setWeightOptions] = useState([]);
  const currentUserType = useSelector(userTypeSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!selectedFaq) {
      setFormValues({
        question: '',
        answer: '',
        category: '',
        weight: null,
      });
      setWeightOptions([]);
      return;
    }
    getWeightOptions(selectedFaq.category, selectedFaq.weight);
    setFormValues({
      question: selectedFaq.question,
      answer: selectedFaq.answer,
      category: selectedFaq.category,
      weight: selectedFaq.weight,
    });
  }, [selectedFaq]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    if (field === 'category') {
      const isEdit = !!selectedFaq;
      const isSameCategory = value === selectedFaq?.category;
      // Determine weight options based on whether we're editing an existing FAQ and if the category has changed
      // If editing and category is unchanged, include the current weight to allow it to remain the same
      // If editing and category is changed, do not include any weight (set to null)
      const currentWeight = isEdit && isSameCategory ? selectedFaq.weight : null;
      getWeightOptions(value, currentWeight);
      setFormValues((prev) => ({
        ...prev,
        category: value,
        weight: currentWeight !== null ? prev.weight : null,
      }));
      return;
    }
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClose = () => {
    setErrors({});
    setFormValues({
      question: '',
      answer: '',
      category: '',
      weight: null,
    });
    setWeightOptions([]);
    onClose();
  };

  const handleSave = () => {
    const formErrors = {};

    if (!formValues.question.trim()) {
      formErrors.question = fieldRequired('Question');
    }

    if (!formValues.answer.trim()) {
      formErrors.answer = fieldRequired('Answer');
    }

    if (!formValues.category.trim()) {
      formErrors.category = fieldRequired('Category');
    }

    if (formValues.weight == null || formValues.weight === '') {
      formErrors.weight = fieldRequired('Weight');
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      return;
    }

    if (selectedFaq) {
      updateFaqItems({
        ...selectedFaq,
        ...formValues,
      });
    } else {
      addFaqItems(formValues);
    }
  };

  const updateFaqItems = async (faqItem) => {
    if (currentUserType.type !== 'internal') return;
    return validateAuth(async (session) => {
      try {
        await axios.put(`${process.env.REACT_APP_AWS_API_URL}/help/faq/${faqItem.id}`, faqItem, {
          headers: {
            Authorization: session.getIdToken().getJwtToken(),
          },
          timeout: 15000,
        });
        dispatch(notificationSuccess('FAQ item updated successfully'));
        handleClose();
      } catch (error) {
        dispatch(notificationError('Error updating FAQ item'));
      }
    });
  };

  const addFaqItems = async (faqItem) => {
    if (currentUserType.type !== 'internal') return;

    return validateAuth(async (session) => {
      try {
        await axios.post(`${process.env.REACT_APP_AWS_API_URL}/help/faq`, faqItem, {
          headers: {
            Authorization: session.getIdToken().getJwtToken(),
          },
          timeout: 15000,
        });
        dispatch(notificationSuccess('FAQ item added successfully'));
        handleClose();
      } catch (error) {
        dispatch(notificationError('Error adding FAQ item'));
      }
    });
  };

  // Weight range from 1 - 50 for each category, with 1 being the highest priority
  // Occupied weights are determined by existing FAQ items in the same category, and are fetched from the backend
  // The current weight of the FAQ being edited is included in the options to allow it to remain unchanged if needed
  const fetchWeightOptions = async (category) => {
    return validateAuth(async (session) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_AWS_API_URL}/help/faq/weights?category=${category}`,
          {
            headers: {
              Authorization: session.getIdToken().getJwtToken(),
            },
            timeout: 15000,
          }
        );
        return response.data.weights;
      } catch (error) {
        return [];
      }
    });
  };

  const getWeightOptions = async (category, weight) => {
    if (!category) {
      setWeightOptions([]);
      return;
    }
    const options = await fetchWeightOptions(category);
    const merged = weight != null ? [...options, weight] : options;
    const sorted = [...new Set(merged)].sort((a, b) => a - b);
    setWeightOptions(sorted);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit FAQ</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          autoFocus
          margin="normal"
          label={`Question (${formValues.question.length}/${QUESTION_MAX_CHAR_LIMIT})`}
          value={formValues.question}
          onChange={handleChange('question')}
          error={Boolean(errors.question)}
          helperText={errors.question}
          slotProps={{
            htmlInput: { maxLength: QUESTION_MAX_CHAR_LIMIT },
          }}
        />
        <TextField
          fullWidth
          margin="normal"
          label={`Answer (${formValues.answer.length}/${ANSWER_MAX_CHAR_LIMIT})`}
          value={formValues.answer}
          onChange={handleChange('answer')}
          error={Boolean(errors.answer)}
          helperText={errors.answer}
          multiline
          minRows={4}
          slotProps={{
            htmlInput: { maxLength: ANSWER_MAX_CHAR_LIMIT },
          }}
        />
        <TextField
          select
          fullWidth
          margin="normal"
          label="Category"
          value={formValues.category}
          onChange={handleChange('category')}
          error={Boolean(errors.category)}
          helperText={errors.category}
        >
          {FAQ_Categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          type="number"
          margin="normal"
          label="Weight"
          value={formValues.weight != null ? formValues.weight : ''}
          onChange={handleChange('weight')}
          error={Boolean(errors.weight)}
          helperText={errors.weight}
        >
          {weightOptions.map((weight) => (
            <MenuItem key={weight} value={weight}>
              {weight}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="tertiary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FaqModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedFaq: PropTypes.shape({
    answer: PropTypes.string,
    category: PropTypes.string,
    question: PropTypes.string,
    weight: PropTypes.number,
  }),
};
